import { Link } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'

export default function ForDevelopersPage() {
  const { user, profile } = useAuth()
  const isAuthenticated = !!user
  const isDeveloper = profile?.role === 'developer'

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-b border-secondary-200">
        <div className="container-custom py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
              Sell Pre-Construction Units Faster with Beaver
            </h1>
            <p className="text-xl text-secondary-700 mb-8 leading-relaxed">
              Connect with qualified buyers early, reduce financial risk, and speed up your project timeline.
              Beaver is the modern marketplace for pre-construction property sales.
            </p>
            <div className="flex flex-wrap gap-4">
              {!isAuthenticated ? (
                <>
                  <Link to="/signup" className="btn-primary px-8 py-3 text-lg">
                    Get Started
                  </Link>
                  <Link to="/signin" className="btn-outline px-8 py-3 text-lg">
                    Sign In
                  </Link>
                </>
              ) : isDeveloper ? (
                <Link to="/dashboard/developer" className="btn-primary px-8 py-3 text-lg">
                  Go to Dashboard
                </Link>
              ) : (
                <Link to="/signup" className="btn-primary px-8 py-3 text-lg">
                  Register as Developer
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="container-custom py-16 md:py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Why Developers Choose Beaver
          </h2>
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            We help you de-risk projects and accelerate sales with our purpose-built platform
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-secondary-900 mb-3">
              Reduce Financial Risk
            </h3>
            <p className="text-secondary-600">
              Secure pre-sales before breaking ground. Lock in buyer commitments early to reduce exposure and secure financing.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-secondary-900 mb-3">
              Reach Qualified Buyers
            </h3>
            <p className="text-secondary-600">
              Access a curated marketplace of serious buyers actively looking for pre-construction opportunities.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-secondary-900 mb-3">
              Accelerate Sales Timeline
            </h3>
            <p className="text-secondary-600">
              Speed up your sales cycle with streamlined processes, automated workflows, and direct buyer communication.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-secondary-50 py-16 md:py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Platform Features
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Everything you need to manage and sell your pre-construction inventory
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  Project Management Dashboard
                </h3>
                <p className="text-secondary-600">
                  Manage multiple projects, track reservations, and monitor sales performance from one central hub.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  Direct Buyer Communication
                </h3>
                <p className="text-secondary-600">
                  Built-in messaging system to communicate directly with interested buyers and answer questions.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  Analytics & Insights
                </h3>
                <p className="text-secondary-600">
                  Track buyer interest, conversion rates, and market demand with comprehensive analytics tools.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  Reservation Management
                </h3>
                <p className="text-secondary-600">
                  Handle reservations, deposits, and unit holds with automated workflows and tracking.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  Visual Showcasing
                </h3>
                <p className="text-secondary-600">
                  Upload floor plans, renderings, and project photos to create compelling listings that attract buyers.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  Project Timeline Updates
                </h3>
                <p className="text-secondary-600">
                  Keep buyers informed with milestone tracking and automated progress updates throughout construction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="container-custom py-16 md:py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            Flexible pricing designed for your success. No hidden fees.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Marketing Fee */}
            <div className="card p-8 border-2 border-emerald-200 bg-emerald-50">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-secondary-900 mb-1">
                    $1,000
                  </h3>
                  <p className="text-sm text-secondary-600">per unit (recommended)</p>
                </div>
              </div>
              <h4 className="text-lg font-semibold text-secondary-900 mb-3">
                Marketing Investment
              </h4>
              <p className="text-secondary-700 mb-4">
                Upfront investment to promote your project to qualified buyers. You choose the amount that fits your marketing budget.
              </p>
              <ul className="space-y-2 text-sm text-secondary-700">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Calculated per unit in project</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Paid upfront at project listing</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Targeted marketing campaigns</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Premium marketplace placement</span>
                </li>
              </ul>
            </div>

            {/* Platform Fee */}
            <div className="card p-8 border-2 border-blue-200 bg-blue-50">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-secondary-900 mb-1">
                    $50
                  </h3>
                  <p className="text-sm text-secondary-600">per unit per month</p>
                </div>
              </div>
              <h4 className="text-lg font-semibold text-secondary-900 mb-3">
                Platform Fee
              </h4>
              <p className="text-secondary-700 mb-4">
                Monthly fee for each active unit listing on the platform.
              </p>
              <ul className="space-y-2 text-sm text-secondary-700">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Ongoing platform maintenance</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Support and updates</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Direct buyer communication</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Capped at $1,000/project/month</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 p-6 bg-secondary-50 rounded-lg">
            <h4 className="text-lg font-semibold text-secondary-900 mb-2 flex items-center">
              <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Example Pricing
            </h4>
            <p className="text-secondary-700 mb-3">
              For a 50-unit development:
            </p>
            <ul className="space-y-2 text-sm text-secondary-700">
              <li className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>Marketing investment: $50,000 upfront (50 units × $1,000 recommended)</span>
              </li>
              <li className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>Platform fee: $1,000/month (capped at maximum)</span>
              </li>
              <li className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>Your marketing budget funds targeted promotion to reach serious buyers</span>
              </li>
            </ul>
          </div>

          <div className="mt-6 p-6 bg-teal-50 rounded-lg border border-teal-200">
            <h4 className="text-lg font-semibold text-secondary-900 mb-2 flex items-center">
              <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Work Your Way
            </h4>
            <p className="text-secondary-700">
              You have complete flexibility in how you sell. Handle all buyer interactions yourself through the platform,
              work with real estate agents from the start, or bring in an agent at any stage of the process.
              The platform supports direct developer-to-buyer communication while giving you the option to involve agents whenever you choose.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-secondary-50 py-16 md:py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Get started in minutes and start connecting with buyers today
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                    1
                  </div>
                </div>
                <div className="pt-2">
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                    Create Your Account
                  </h3>
                  <p className="text-secondary-600">
                    Sign up as a developer and complete your company profile. Add your team members and set up your dashboard.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                    2
                  </div>
                </div>
                <div className="pt-2">
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                    List Your Project
                  </h3>
                  <p className="text-secondary-600">
                    Add project details, upload floor plans and renderings, and configure your units with pricing and availability.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                    3
                  </div>
                </div>
                <div className="pt-2">
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                    Connect with Buyers
                  </h3>
                  <p className="text-secondary-600">
                    Qualified buyers discover your project, ask questions, and submit reservations. Communicate directly through the platform.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                    4
                  </div>
                </div>
                <div className="pt-2">
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                    Close the Deal
                  </h3>
                  <p className="text-secondary-600">
                    Manage reservations, track deposits, and transition buyers to your sales process. Keep buyers updated on project progress.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-600 py-16 md:py-20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-emerald-50 mb-8 max-w-2xl mx-auto">
            Join the developers already using Beaver to sell pre-construction units faster and reduce project risk.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            {!isAuthenticated ? (
              <>
                <Link to="/signup" className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-8 py-3 rounded-lg text-lg transition-colors">
                  Create Account
                </Link>
                <a
                  href="mailto:hello@beaver.nz"
                  className="bg-emerald-700 text-white hover:bg-emerald-800 font-semibold px-8 py-3 rounded-lg text-lg transition-colors border-2 border-white"
                >
                  Contact Sales
                </a>
              </>
            ) : isDeveloper ? (
              <Link to="/dashboard/developer" className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-8 py-3 rounded-lg text-lg transition-colors">
                Go to Dashboard
              </Link>
            ) : (
              <Link to="/signup" className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-8 py-3 rounded-lg text-lg transition-colors">
                Register as Developer
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
